import { useMemo } from "react";
import { useSelector } from "react-redux";

import { Container, Table, TableBody, Typography } from "@mui/material";

import EChartsReact from "echarts-for-react";

import NoDataRow from "../../NoDataRow";
import HoverPaper from "../HoverPaper";

import {
    DISPLAY_ELEMENT_HELPER,
    COMPARISON_FILTER,
    DEFAULT_COMPARISON,
} from "../../../helpers/filters";
import { TARGET_OFF, TARGET_FILTER } from "../../../helpers/targets";
import Colours from "../../../helpers/colours";
import { tooltip } from "../../../helpers/eCharts";
import { fns } from "../../../helpers/common";
import { newDateUTC } from "../../../helpers/dates";

export const REPORT_CHART_PERFORMANCE_VS = "REPORT_CHART_PERFORMANCE_VS";
export const ReportChartPerformanceVS = ({
    js,
    title,
    data,
    chartType = "line",
    metric = "bookingsTotal",
    total = false,
    emptyMessage = "No data available for this selection",
}) => {
    const { id: hotelID } = useSelector((state) => state.hotelID);

    const targetValue = js?.filters?.Target ?? TARGET_OFF;
    const comparisonLabel =
        targetValue !== TARGET_OFF
            ? `Target ${TARGET_FILTER[targetValue]?.label ?? TARGET_FILTER[TARGET_OFF].label}`
            : (COMPARISON_FILTER[js?.filters?.Comparison ?? DEFAULT_COMPARISON]
                  ?.label ?? COMPARISON_FILTER[DEFAULT_COMPARISON].label);

    const formatDate = (keys) => {
        const obj = Object.fromEntries(keys);
        const year = obj.Year;
        const month = obj.Month;
        const day = obj.Day;

        const date = new Date(`${year}-${month}-${day}`);

        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const { xAxisLabels, dataActual, dataSTLY, totalActual } = useMemo(() => {
        if (!data || Object.keys(data).length === 0) {
            return { xAxisLabels: [], dataActual: [], dataSTLY: [] };
        }
        const sortedKeys = Object.keys(data).sort((a, b) => {
            let aKeys = Object.fromEntries(data[a].keys);
            let bKeys = Object.fromEntries(data[b].keys);
            return (
                newDateUTC(aKeys.Year, aKeys.Month, aKeys.Day) -
                newDateUTC(bKeys.Year, bKeys.Month, bKeys.Day)
            );
        });

        const validGroups = Object.keys(data).filter((group) => {
            const result = data[group]?.result;
            return result && Object.keys(result).length > 0;
        });

        const xAxisLabels = sortedKeys.map((e) => formatDate(data[e].keys));
        let dataActual = sortedKeys.map((e) => data[e].result?.[metric] ?? 0);
        if (dataActual.every((v) => v === 0)) {
            dataActual = [];
        }

        const dataSTLY = sortedKeys.map(
            (e) => data[e].resultOneYearBefore?.[metric] ?? 0,
        );

        const totalActual = dataActual.reduce((sum, v) => sum + (v || 0), 0);
        return { xAxisLabels, dataActual, dataSTLY, totalActual };
    }, [data, metric]);

    const option = useMemo(() => {
        return {
            tooltip: tooltip,

            legend: {
                bottom: 0,
                left: "center",
                data: ["Actual", comparisonLabel],
                icon: "roundRect",
            },

            xAxis: {
                type: "category",
                data: xAxisLabels,
            },

            yAxis: {
                type: "value",
            },

            series: [
                {
                    type: chartType,
                    name: comparisonLabel,
                    data: dataSTLY,
                    smooth: true,
                    itemStyle: {
                        color: Colours.graphDisabled,
                    },
                },
                {
                    type: chartType,
                    name: "Actual",
                    data: dataActual,
                    itemStyle: {
                        color: Colours.graphSold,
                    },
                },
            ],
        };
    }, [xAxisLabels, dataActual, dataSTLY]);

    return (
        <>
            {title && (
                <Typography fontWeight="bold" sx={{ pt: 2 }}>
                    {title}
                </Typography>
            )}
            <Container
                maxWidth={false}
                component={HoverPaper}
                sx={{ p: 2, backgroundColor: Colours.notificationCard }}
            >
                {dataActual.length > 0 ? (
                    <>
                        {total && (
                            <Typography>
                                Total:{" "}
                                <span style={{ fontWeight: "bold" }}>
                                    {fns(
                                        hotelID,
                                        totalActual,
                                        DISPLAY_ELEMENT_HELPER[metric].format ??
                                            {},
                                        false,
                                    )}
                                </span>
                            </Typography>
                        )}

                        <EChartsReact option={option} />
                    </>
                ) : (
                    <Table>
                        <TableBody>
                            <NoDataRow text={emptyMessage} />
                        </TableBody>
                    </Table>
                )}
            </Container>
        </>
    );
};

export default ReportChartPerformanceVS;
